import {
    patchState,
    signalStore,
    withComputed,
    withHooks,
    withLinkedState,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    Observable,
    pairwise,
    switchMap,
    from,
    shareReplay,
    filter,
    EMPTY,
    distinctUntilChanged,
    map,
} from 'rxjs';
import { AGENT_SIGNALR_HUB, LIVE_SIGNALR_HUB } from '@dxp/core/signalr';
import {
    IWorkItem,
    IWorkItems,
    IWorkItemWithConversation,
    IConversationCustomer,
    IAgentClientState,
    IWorkItemClosedEvent,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import {
    diffWorkItems,
    mapByWorkItemId,
    transformWorkItem,
    mergeEnrichedCustomers,
    upsertWorkItems,
    hasCustomerChanged,
    calculateDisplayCustomer,
    transformWorkItemWithConversation,
} from './workitems.helpers';
import { IWorkItemChangeEvent } from './workitems.models';

interface IWorkItemStoreState {
    activeConversationId: string | null;
    activeWorkItemId: string | null;
    myWorkItems: IWorkItem[];
    systemWorkItems: IWorkItem[];
    loading: boolean;
}

const initialState: IWorkItemStoreState = {
    activeConversationId: null,
    activeWorkItemId: null,
    myWorkItems: [],
    systemWorkItems: [],
    loading: true,
};

export const WorkItemsStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('WorkitemStore'),
    withState<IWorkItemStoreState>(initialState),
    withComputed(store => ({
        ready: computed(() => !store.loading()),
        workItems: computed(() => store.myWorkItems()),
        activeWorkItem: computed(() =>
            store
                .myWorkItems()
                .find(w => w.workItemId === store.activeWorkItemId()),
        ),
    })),
    withLinkedState(store => {
        return {
            activeConversation: () =>
                store.activeWorkItem()?.conversations[0] ?? null,
            activeMonitoredTask: () =>
                store
                    .myWorkItems()
                    .filter(
                        workItem =>
                            workItem.channelIdentifier === 'Monitor' &&
                            workItem.primaryConversationId ===
                                store.activeConversationId(),
                    ) ?? null,
            activeMonitoredTasks: () =>
                store
                    .myWorkItems()
                    .filter(
                        workItem => workItem.channelIdentifier === 'Monitor',
                    ) ?? null,
            hasActiveMonitoredTasks: () =>
                store
                    .myWorkItems()
                    .filter(
                        workItem => workItem.channelIdentifier === 'Monitor',
                    ).length > 0,
        };
    }),
    withProps(store => {
        const loading$ = toObservable(store.loading).pipe(
            distinctUntilChanged(),
        );

        const workItems$ = toObservable(store.myWorkItems);

        const workItemEvents$: Observable<IWorkItemChangeEvent> = loading$.pipe(
            switchMap(loading => {
                if (loading) return EMPTY;

                return workItems$.pipe(
                    pairwise(),
                    switchMap(([prev, next]) =>
                        from(diffWorkItems(prev, next)),
                    ),
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        const onWorkItemAdded$ = workItemEvents$.pipe(
            filter(e => e.type === 'added'),
            map(e => e.workitem),
        );

        const onWorkItemRemoved$ = workItemEvents$.pipe(
            filter(e => e.type === 'removed'),
            map(e => e.workitem),
        );

        const onWorkItemStateChanged$ = workItemEvents$.pipe(
            filter(e => e.type === 'state'),
            map(e => e.workitem),
        );

        const onWorkItemCustomerChanged$ = workItemEvents$.pipe(
            filter(e => e.type === 'customer'),
            map(e => e.workitem),
        );

        return {
            onWorkItemAdded$: onWorkItemAdded$,
            onWorkItemRemoved$: onWorkItemRemoved$,
            onWorkItemStateChanged$: onWorkItemStateChanged$,
            onWorkItemCustomerChanged$: onWorkItemCustomerChanged$,
        };
    }),
    withMethods(store => ({
        updateActiveWorkId(workItemId: string) {
            updateState(store, 'UPDATE_ACTIVE_WORKITEM_ID', {
                activeWorkItemId: workItemId,
            });
        },
        updateActiveConversationId(conversationId: string) {
            updateState(store, 'UPDATE_ACTIVE_CONVERSATION_ID', {
                activeConversationId: conversationId,
            });
        },
        updateAgentWorkItems(workItems: IWorkItems) {
            updateState(store, 'UPDATE_MY_WORKITEMS', state => {
                const existing = state.myWorkItems;
                const existingById = mapByWorkItemId(existing);

                const incoming = Object.values(workItems).map(src => {
                    const base = transformWorkItem(src);
                    const prev = existingById.get(base.workItemId);
                    return prev ? mergeEnrichedCustomers(prev, base) : base;
                });

                const { next, changed } = upsertWorkItems(existing, incoming);

                if (!changed) return state;

                return {
                    ...state,
                    myWorkItems: next,
                };
            });
        },
        enrichAgentWorkItems(
            workItemsWithConversations: IWorkItemWithConversation[],
        ) {
            updateState(store, 'UPDATE_MY_WORKITEMS', state => {
                const existing = state.myWorkItems;

                if (
                    existing.length === 0 ||
                    workItemsWithConversations.length === 0
                ) {
                    return state;
                }

                const wiwcByWorkItemId = new Map<
                    string,
                    IWorkItemWithConversation
                >();
                for (const wi of workItemsWithConversations) {
                    if (wi?.id) wiwcByWorkItemId.set(wi.id, wi);
                }

                if (wiwcByWorkItemId.size === 0) return state;

                let anyChanged = false;

                const next = existing.map(w => {
                    const wiwc = wiwcByWorkItemId.get(w.workItemId);
                    if (!wiwc) return w;

                    const incomingConvs = wiwc.conversations ?? [];
                    if (incomingConvs.length === 0) return w;

                    const incomingCustomerByConvId = new Map<
                        string,
                        IConversationCustomer | undefined
                    >();
                    for (const c of incomingConvs) {
                        if (c?.id)
                            incomingCustomerByConvId.set(c.id, c.customer);
                    }

                    if (incomingCustomerByConvId.size === 0) return w;

                    const localConvs = w.conversations ?? [];
                    if (localConvs.length === 0) return w;

                    let overlaps = false;
                    for (const c of localConvs) {
                        const id = c?.conversationId;
                        if (id && incomingCustomerByConvId.has(id)) {
                            overlaps = true;
                            break;
                        }
                    }
                    if (!overlaps) return w;

                    const primaryId = w.primaryConversationId;

                    let conversationsChanged = false;
                    let nextPrimaryCustomer: IConversationCustomer | undefined =
                        w.primaryConversationCustomer;

                    const nextConversations = localConvs.map(c => {
                        const convId = c.conversationId;
                        if (!convId) return c;

                        const incomingCustomer =
                            incomingCustomerByConvId.get(convId);

                        if (
                            incomingCustomer === undefined &&
                            !incomingCustomerByConvId.has(convId)
                        ) {
                            if (convId === primaryId)
                                nextPrimaryCustomer = c.customer;
                            return c;
                        }

                        const nextCustomer = incomingCustomer;

                        if (convId === primaryId)
                            nextPrimaryCustomer = nextCustomer ?? undefined;

                        if (!hasCustomerChanged(c.customer, nextCustomer))
                            return c;

                        conversationsChanged = true;
                        return { ...c, customer: nextCustomer };
                    });

                    const primaryChanged = hasCustomerChanged(
                        w.primaryConversationCustomer,
                        nextPrimaryCustomer,
                    );

                    const candidate: IWorkItem =
                        conversationsChanged || primaryChanged
                            ? {
                                  ...w,
                                  conversations: nextConversations,
                                  primaryConversationCustomer:
                                      nextPrimaryCustomer,
                              }
                            : w;

                    const nextDisplayCustomer = nextPrimaryCustomer
                        ? calculateDisplayCustomer(
                              candidate.primaryMediaType,
                              candidate.customerIdentifier,
                              nextPrimaryCustomer,
                          )
                        : w.displayCustomer;

                    const displayChanged =
                        w.displayCustomer !== nextDisplayCustomer;

                    if (
                        !conversationsChanged &&
                        !primaryChanged &&
                        !displayChanged
                    ) {
                        return w;
                    }

                    anyChanged = true;

                    if (candidate === w && displayChanged) {
                        return { ...w, displayCustomer: nextDisplayCustomer };
                    }

                    return displayChanged
                        ? { ...candidate, displayCustomer: nextDisplayCustomer }
                        : candidate;
                });

                if (!anyChanged) return state;

                return {
                    ...state,
                    myWorkItems: next,
                };
            });
        },
        removeWorkItem(workItemId: string) {
            updateState(store, 'REMOVE_MY_WORKITEM', state => {
                const activeWorkItemId = state.activeWorkItemId;
                const activeConversationId = state.activeConversationId;
                const removedWasActiveWorkItem =
                    activeWorkItemId === workItemId;
                const removed = state.myWorkItems.find(
                    w => w.workItemId === workItemId,
                );

                if (!removed) return state;

                const removedContainedActiveConversation =
                    !!activeConversationId &&
                    removed.conversations?.some(
                        c => c.conversationId === activeConversationId,
                    ) === true;

                const nextWorkItems = state.myWorkItems.filter(
                    w => w.workItemId !== workItemId,
                );

                if (nextWorkItems.length === state.myWorkItems.length)
                    return state;

                return {
                    ...state,
                    myWorkItems: nextWorkItems,
                    activeWorkItemId: removedWasActiveWorkItem
                        ? null
                        : activeWorkItemId,
                    activeConversationId: removedContainedActiveConversation
                        ? null
                        : activeConversationId,
                };
            });
        },
        updateSystemWorkItems(workItems: IWorkItemWithConversation[]) {
            updateState(store, 'UPDATE_SYSTEM_WORKITEMS', state => {
                const existing = state.systemWorkItems;

                const incoming = workItems
                    .filter(src => src.status !== 'Closed')
                    .map(src => transformWorkItemWithConversation(src));

                const { next, changed } = upsertWorkItems(
                    existing,
                    incoming,
                    true,
                );

                if (!changed) return state;

                return {
                    ...state,
                    systemWorkItems: next,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            destroyRef = inject(DestroyRef),
            agentHubService = inject(AGENT_SIGNALR_HUB),
            liveHubService = inject(LIVE_SIGNALR_HUB),
        ) {
            agentHubService.agentState$
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe((agentState: IAgentClientState) => {
                    store.updateAgentWorkItems(agentState.workItems);
                    patchState(store, { loading: false });
                });

            agentHubService.workItemClosed$
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe((workItem: IWorkItemClosedEvent) => {
                    store.removeWorkItem(workItem.id);
                });

            liveHubService.workItemsWithConversations$
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe(
                    (
                        workItemsWithConversations: IWorkItemWithConversation[],
                    ) => {
                        store.enrichAgentWorkItems(workItemsWithConversations);
                        store.updateSystemWorkItems(workItemsWithConversations);
                    },
                );
        },
    }),
);
